"use client";

import { Button } from "@repo/ui";
import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";

interface Guest {
  name: string;
  bio?: string;
  photo?: string;
  website?: string;
  social?: {
    linkedin?: string;
    twitter?: string;
    instagram?: string;
  };
}

interface PodcastEpisode {
  id: string;
  title: string;
  description: string;
  audioUrl: string;
  coverImageUrl: string | null;
  guests: Guest[] | null;
  duration: number;
  publishedAt: string;
  spotifyUrl: string | null;
  applePodcastsUrl: string | null;
  googlePodcastsUrl: string | null;
  youtubeUrl: string | null;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function PodcastPage() {
  const router = useRouter();
  const [episodes, setEpisodes] = useState<PodcastEpisode[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchEpisodes() {
      try {
        const response = await fetch("/api/podcast");
        const data = await response.json();

        if (data.success) {
          setEpisodes(data.episodes);
        }
      } catch (error) {
        console.error("Failed to fetch podcast episodes:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchEpisodes();
  }, []);

  // Helper function to format duration from seconds to MM:SS
  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // Helper function to format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
     timeZone: 'America/Toronto'});
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-primary-50/30 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24 px-4">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-72 h-72 bg-primary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
          <div className="absolute top-40 left-10 w-72 h-72 bg-secondary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-20 left-1/2 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-primary-100 mb-6">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-primary-500"></span>
              </span>
              <span className="text-sm font-semibold text-primary-700 uppercase tracking-wide">New Podcast</span>
            </div>
            <img
              src="https://uhjrqinjjozqxcmxrpuq.supabase.co/storage/v1/object/public/public-assets/web-assets/podcast-banner.png"
              alt="Permission to Heal"
              className="max-w-2xl w-full mx-auto mb-8 px-4"
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Podcast Cover */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary-400 to-secondary-400 rounded-3xl blur-2xl opacity-20"></div>
                <img
                  src="https://uhjrqinjjozqxcmxrpuq.supabase.co/storage/v1/object/public/public-assets/web-assets/podcast.jpeg"
                  alt="Permission to Heal Podcast"
                  className="relative w-80 h-80 md:w-96 md:h-96 rounded-2xl shadow-2xl object-cover"
                />
              </div>
            </div>

            {/* Podcast Info */}
            <div className="space-y-6 text-center lg:text-left">
              <p className="text-xl text-gray-700 leading-relaxed">
                We all have stories that we've tucked away. Ones that feel too heavy, too messy, too much. Permission to Heal was created to bring those stories into the light, because we believe that <span className="font-semibold text-primary-700">shame shrinks when stories are shared in safe spaces</span>.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Here, real people share their brave human stories and mental health experts join us to provide you with tangible tips and share insights that can help you thrive, not just survive.
              </p>

              {/* Listen Platforms */}
              <div className="pt-6">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Listen on your favorite platform</p>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => window.open("https://open.spotify.com/show/6T9MYL2UGS2NnZLgeWp530?si=ecd234cddb42412c", "_blank")}
                    className="flex items-center gap-2 bg-white hover:bg-primary-50 border-gray-200 hover:border-primary-300 transition-all"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                    </svg>
                    Spotify
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => window.open("https://podcasts.apple.com/ca/podcast/permission-to-heal/id1859455656", "_blank")}
                    className="flex items-center gap-2 bg-white hover:bg-primary-50 border-gray-200 hover:border-primary-300 transition-all"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                    </svg>
                    Apple
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => window.open("https://www.youtube.com/@PermissiontoHealPodcastAlli", "_blank")}
                    className="flex items-center gap-2 bg-white hover:bg-primary-50 border-gray-200 hover:border-primary-300 transition-all"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                    YouTube
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Episodes */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          {!loading && episodes.length > 0 && (
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Latest Episodes
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Dive into conversations that matter. Real stories, real healing.
              </p>
            </div>
          )}

          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600"></div>
              <p className="mt-6 text-gray-600 text-lg">Loading episodes...</p>
            </div>
          ) : episodes.length === 0 ? (
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-100/50 to-secondary-100/50 rounded-3xl blur-2xl"></div>
              <div className="relative text-center py-20 bg-white rounded-3xl shadow-xl border border-gray-100">
                <div className="max-w-lg mx-auto px-6">
                  <div className="mb-8 relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-32 h-32 bg-primary-100 rounded-full blur-2xl"></div>
                    </div>
                    <svg className="relative w-24 h-24 mx-auto text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Coming Soon</h3>
                  <p className="text-gray-600 text-lg leading-relaxed mb-8">
                    We're working on bringing you inspiring stories and expert insights. Subscribe now so you don't miss our first episode!
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center">
                    <Button
                      size="lg"
                      onClick={() => window.open("https://open.spotify.com/show/6T9MYL2UGS2NnZLgeWp530?si=ecd234cddb42412c", "_blank")}
                      className="bg-primary-600 hover:bg-primary-700"
                    >
                      Subscribe on Spotify
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={() => window.open("https://podcasts.apple.com/ca/podcast/permission-to-heal/id1859455656", "_blank")}
                    >
                      Subscribe on Apple
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {episodes.map((episode) => (
                <div
                  key={episode.id}
                  onClick={() => router.push(`/podcast/${episode.id}`)}
                  className="group cursor-pointer relative"
                >
                  {/* Glow effect on hover */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-400 to-secondary-400 rounded-2xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>

                  <div className="relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 border border-gray-100">
                    {/* Episode Cover */}
                    <div className="relative aspect-[16/9] bg-gradient-to-br from-primary-100 via-secondary-50 to-primary-50 overflow-hidden">
                      {episode.coverImageUrl ? (
                        <>
                          <img
                            src={episode.coverImageUrl}
                            alt={episode.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg
                            className="w-16 h-16 text-primary-400"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                          </svg>
                        </div>
                      )}

                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                        <div className="relative">
                          <div className="absolute inset-0 bg-white rounded-full blur-xl opacity-50"></div>
                          <div className="relative bg-white rounded-full p-3 shadow-2xl transform scale-90 group-hover:scale-100 transition-transform duration-500">
                            <svg
                              className="w-8 h-8 text-primary-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Duration Badge */}
                      <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm px-2.5 py-1 rounded-full">
                        <span className="text-white text-xs font-medium">{formatDuration(episode.duration)}</span>
                      </div>
                    </div>

                    {/* Episode Info */}
                    <div className="p-4 space-y-3">
                      {/* Date */}
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatDate(episode.publishedAt)}
                      </div>

                      {/* Title */}
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-tight">
                        {episode.title}
                      </h3>

                      {/* Description */}
                      <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                        {episode.description}
                      </p>

                      {/* Guest Info */}
                      {episode.guests && Array.isArray(episode.guests) && episode.guests.length > 0 && episode.guests[0] && (
                        <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                          {episode.guests[0].photo ? (
                            <img
                              src={episode.guests[0].photo}
                              alt={episode.guests[0].name}
                              className="w-8 h-8 rounded-full object-cover ring-2 ring-primary-100"
                            />
                          ) : (
                            <div className="w-8 h-8 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-900 truncate">
                              {episode.guests.map(g => g.name).join(', ')}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Platform Buttons */}
                      <div className="flex gap-1.5 pt-2">
                        {episode.spotifyUrl && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(episode.spotifyUrl!, "_blank");
                            }}
                            className="flex-1 px-2 py-1.5 bg-gradient-to-r from-primary-50 to-primary-100 hover:from-primary-100 hover:to-primary-200 text-primary-700 text-xs font-medium rounded-lg transition-all flex items-center justify-center gap-1.5 group/btn"
                            title="Listen on Spotify"
                          >
                            <svg className="w-3.5 h-3.5 group-hover/btn:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                            </svg>
                            <span className="hidden sm:inline">Spotify</span>
                          </button>
                        )}
                        {episode.applePodcastsUrl && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(episode.applePodcastsUrl!, "_blank");
                            }}
                            className="flex-1 px-2 py-1.5 bg-gradient-to-r from-secondary-50 to-secondary-100 hover:from-secondary-100 hover:to-secondary-200 text-secondary-700 text-xs font-medium rounded-lg transition-all flex items-center justify-center gap-1.5 group/btn"
                            title="Listen on Apple Podcasts"
                          >
                            <svg className="w-3.5 h-3.5 group-hover/btn:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                            </svg>
                            <span className="hidden sm:inline">Apple</span>
                          </button>
                        )}
                        {episode.youtubeUrl && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(episode.youtubeUrl!, "_blank");
                            }}
                            className="flex-1 px-2 py-1.5 bg-gradient-to-r from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 text-red-700 text-xs font-medium rounded-lg transition-all flex items-center justify-center gap-1.5 group/btn"
                            title="Watch on YouTube"
                          >
                            <svg className="w-3.5 h-3.5 group-hover/btn:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                            </svg>
                            <span className="hidden sm:inline">YouTube</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* About the Podcast */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            About Your Host
          </h2>
          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Kelly's Photo */}
            <div className="flex-shrink-0">
              <img
                src="https://uhjrqinjjozqxcmxrpuq.supabase.co/storage/v1/object/public/public-assets/profile-images/2030ef89-be65-4ea0-8186-54adb9e921f3/headshot-1757538513469.png?t=1757538514644"
                alt="Kelly - Host of Permission to Heal"
                className="w-48 h-48 md:w-64 md:h-64 rounded-full object-cover shadow-lg border-4 border-white"
              />
            </div>
            {/* Host Description */}
            <div className="flex-1 text-center md:text-left">
              <p className="text-lg text-gray-700 leading-relaxed">
                Your host, Kelly, is a Registered Psychotherapist with Alli Therapy. Her vision is simple: a world where we can speak freely, connect deeply, and heal in community. This podcast is not about perfection or quick fixes. It is about finally giving yourself what you have needed all along: permission to be fully, beautifully human. Permission to heal.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Subscribe CTA */}
      <section className="py-20 px-4 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Never Miss an Episode
          </h2>
          <p className="text-xl mb-8 text-primary-50">
            Subscribe to Permission to Heal and join the conversation on Apple, Spotify, YouTube, and TikTok.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-primary-700"
              onClick={() => {
                window.open("https://open.spotify.com/show/6T9MYL2UGS2NnZLgeWp530?si=ecd234cddb42412c", "_blank");
              }}
            >
              Subscribe on Spotify
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-primary-700"
              onClick={() => {
                window.open("https://podcasts.apple.com/ca/podcast/permission-to-heal/id1859455656", "_blank");
              }}
            >
              Subscribe on Apple Podcasts
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-primary-700"
              onClick={() => {
                window.open("https://www.youtube.com/@PermissiontoHealPodcastAlli", "_blank");
              }}
            >
              Subscribe on YouTube
            </Button>
          </div>
          <div className="mt-16 pt-12 border-t border-primary-500">
            <h3 className="text-2xl font-bold text-white mb-3">
              Ready to start your therapy journey?
            </h3>
            <p className="text-lg text-primary-100 mb-6 max-w-2xl mx-auto">
              Find a therapist who understands you. Take our quick matching quiz to connect with the right therapist for your needs.
            </p>
            <Button
              size="lg"
              className="bg-white hover:bg-primary-700 hover:border-primary-700 font-semibold px-8 py-6 text-lg !text-cyan-800 hover:!text-white transition-all duration-200"
              onClick={() => router.push("/quiz")}
            >
              Find Your Therapist
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
